/**
 * Category Registry
 *
 * Central lookup that maps a backend `categoryKey` to its `CategoryConfig`.
 * Every known category has a dedicated config file; any category key that the
 * backend adds in the future will automatically fall through to `DEFAULT_CONFIG`.
 *
 * Usage:
 *   import { getCategoryConfig } from '../categories';
 *   const cfg = getCategoryConfig('infraProfile');
 */

import type { CategoryConfig, ChartDescriptor, ChartDataContext } from './CategoryConfig';

// ── Per-category configs (lazy-loaded from sibling files) ────
import { basicProfileConfig }    from './basicProfile';
import { infraProfileConfig }    from './infraProfile';
import { enrollmentConfig }      from './enrollmentProfile';
import { cwsnInfraConfig }       from './cwsnInfraProfile';
import { cwsnConfig }            from './cwsnProfile';
import { libraryConfig }         from './libraryProfile';
import { sportInfraConfig }      from './sportInfraProfile';
import { ictConfig }             from './ictProfile';

// ═══════════════════════════════════════════════════════════════
// DEFAULT (catch-all) CONFIG
// ═══════════════════════════════════════════════════════════════

export const DEFAULT_CONFIG: CategoryConfig = {
  categoryKey: '__default__',
  label: 'Unknown Category',
  // No chart override → standard bar chart
  // No column override → auto-detect from API response
  // No kpi override → standard single-select dropdown fetched from API
  priority: 999,
};

// ═══════════════════════════════════════════════════════════════
// REGISTRY
// ═══════════════════════════════════════════════════════════════

const REGISTRY: CategoryConfig[] = [
  basicProfileConfig,
  infraProfileConfig,
  enrollmentConfig,
  cwsnInfraConfig,
  cwsnConfig,
  libraryConfig,
  sportInfraConfig,
  ictConfig,
];

/** Map keyed by categoryKey (normalised lowercase, no whitespace) */
const LOOKUP = new Map<string, CategoryConfig>();

const normalise = (s: string) => s.toLowerCase().replace(/[\s_\-]/g, '');

REGISTRY.forEach(cfg => {
  LOOKUP.set(normalise(cfg.categoryKey), cfg);
});

// ═══════════════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════════════

/**
 * Retrieve the `CategoryConfig` for a given category key.
 *
 * - Exact-match lookup first.
 * - Falls back to DEFAULT_CONFIG for any unknown category → data still visible.
 */
export function getCategoryConfig(categoryKey: string | undefined | null): CategoryConfig {
  if (!categoryKey) return DEFAULT_CONFIG;
  const normalised = normalise(categoryKey);
  const found = LOOKUP.get(normalised);
  console.log('[getCategoryConfig]', {
    categoryKey,
    normalised,
    found: found?.categoryKey ?? 'DEFAULT',
    lookupSize: LOOKUP.size,
    lookupKeys: Array.from(LOOKUP.keys()),
  });
  return found ?? DEFAULT_CONFIG;
}

/**
 * Check whether a dedicated config exists for this category.
 */
export function hasDedicatedConfig(categoryKey: string): boolean {
  return LOOKUP.has(normalise(categoryKey));
}

/**
 * All registered configs, sorted by priority.
 */
export function getAllConfigs(): CategoryConfig[] {
  return [...REGISTRY].sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));
}

// Re-export the core types for convenience
export type { CategoryConfig, ChartDescriptor, ChartDataContext };
